import { createStitches as createStitches$1 } from '@stitches/core';
export { defaultThemeMap } from '@stitches/core';

const angular = Symbol.for('sxs.angular');
const internal = Symbol.for('sxs.internal');

var symbols = /*#__PURE__*/Object.freeze({
    __proto__: null,
    angular: angular,
    internal: internal
});

const CLASS_PROPERTY = 'class';
const CHANGE_PREDICATE = 'shouldStitchesClassChange';
function attachStitchesInternals(component, style) {
    component[angular] = style;
    component[internal] = style[internal];
    component.className = style.className;
    component.selector = style.selector;
    component.toString = () => style.selector;
    return component;
}
function decorateNgOnInit(ngOnInit, style) {
    return function (...args) {
        this[CLASS_PROPERTY] = style(this).toString();
        if (!ngOnInit)
            return;
        ngOnInit && ngOnInit.apply(this, args);
    };
}
function decorateNgOnChanges(ngOnChanges, shouldChange, style) {
    return function (...args) {
        if (!shouldChange || shouldChange.apply(this, args)) {
            this[CLASS_PROPERTY] = style(this).toString();
        }
        if (!ngOnChanges)
            return;
        ngOnChanges && ngOnChanges.apply(this, args);
    };
}
function createStyledDecorator() {
    return style => (component) => {
        if (!style)
            return component;
        attachStitchesInternals(component, style);
        component.prototype.ngOnInit = decorateNgOnInit(component.prototype.ngOnInit, style);
        component.prototype.ngOnChanges = decorateNgOnChanges(component.prototype.ngOnChanges, component.prototype[CHANGE_PREDICATE], style);
        return component;
    };
}

function createPropsToCssFunction(media) {
    return (composer) => Object.entries(composer).reduce((rules, [prop, candidate]) => {
        if (candidate == null ||
            typeof candidate !== 'object' ||
            Array.isArray(candidate)) {
            rules[prop] = candidate;
            return rules;
        }
        for (const key in candidate) {
            if (key === '@initial' ||
                !key.startsWith('@') ||
                !media?.[key.substring(1)]) {
                rules[prop] = candidate[key];
                continue;
            }
            const breakpoint = rules[key] ?? {};
            breakpoint[prop] = candidate[key];
            rules[key] = breakpoint;
        }
        return rules;
    }, {});
}

const createStitches = config => {
    const instance = createStitches$1(config);
    instance.Styled = createStyledDecorator();
    instance.propsToCss = createPropsToCssFunction(config?.media);
    return instance;
};

let config;
function getCachedConfig() {
    return config ?? (config = createStitches());
}

//HACK: Zone.js patches the native `Object.defineProperties` function,
// but they introduced a problem with that patch. It uses Object.keys
// to get the properties from the sources, ignoring symbols in the process.
// The native function does support symbols and stitches relies on this
// in their component composition logic. This is why we have to ensure
// `Object.defineProperties` has support for symbols.
// An issue was open in the angular repository, but they dismissed it :(
// https://github.com/angular/angular/issues/31750
function shouldPatchDefineProperties() {
    const properties = Object.defineProperties({}, { [Symbol.iterator]: {} });
    return Object.getOwnPropertySymbols(properties).length === 0;
}
function ensureDefinePropertiesSymbolSupport() {
    if (!shouldPatchDefineProperties())
        return;
    Object.defineProperties = function (obj, props) {
        Object.getOwnPropertyNames(props)
            .concat(Object.getOwnPropertySymbols(props))
            .forEach(function (prop) {
            Object.defineProperty(obj, prop, props[prop]);
        });
        return obj;
    };
}

ensureDefinePropertiesSymbolSupport();
const createTheme = (...args) => getCachedConfig().createTheme(...args);
const globalCss = (...args) => getCachedConfig().globalCss(...args);
const keyframes = (...args) => getCachedConfig().keyframes(...args);
const css = (...args) => getCachedConfig().css(...args);
const Styled = (...args) => getCachedConfig().Styled(...args);

/**
 * Generated bundle index. Do not edit.
 */

export { Styled, createStitches, createTheme, css, globalCss, keyframes };
